import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  buildBreadcrumbs,
  resolveNavigation,
  HOME_PATH,
} from '@platform/entitlements';
import { api, ApiError, SESSION_ENDED_EVENT } from './api';
import type { InstallInfo, PortalModule, SessionUser, SsoProviderStatus } from './api';
import { ModulePlaceholder } from './pages/ModulePlaceholder';
import { StartMenu } from './pages/StartMenu';
import { LoginPage } from './pages/LoginPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { AdminModule } from './pages/admin/AdminModule';
import { AprModule } from './pages/apr/AprModule';
import { AiReadinessModule } from './pages/ai_readiness/AiReadinessModule';
import { SpnModule } from './pages/spn/SpnModule';
import { PortfolioDefinitionModule } from './pages/portfolio_definition/PortfolioDefinitionModule';
import { ProjectManagementModule } from './pages/project_management/ProjectManagementModule';
import { ErpConsolidationModule } from './pages/erp_consolidation/ErpConsolidationModule';
import { ProgramPlanningModule } from './pages/program_planning/ProgramPlanningModule';
import { LicensingModule } from './pages/licensing/LicensingModule';
import { PortalShell } from './shell/PortalShell';

interface PortalState {
  loading: boolean;
  authenticated: boolean;
  error?: string;
  user?: SessionUser;
  install?: InstallInfo;
  ssoProviders?: SsoProviderStatus;
  gatewayUrl?: string;
  modules: PortalModule[];
}

function usePortalData(): PortalState {
  const [state, setState] = useState<PortalState>({
    loading: true,
    authenticated: false,
    modules: [],
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const session = await api.session();
        const modules = session.authenticated ? await api.modules() : { modules: [] };

        if (cancelled) return;
        setState({
          loading: false,
          authenticated: session.authenticated,
          ...(session.user ? { user: session.user } : {}),
          install: session.install,
          ssoProviders: session.ssoProviders,
          ...(session.gatewayUrl ? { gatewayUrl: session.gatewayUrl } : {}),
          modules: modules.modules,
        });
      } catch (error) {
        if (cancelled) return;
        // Surface the failure rather than rendering an empty portal that looks
        // like a client who licensed nothing.
        setState({
          loading: false,
          authenticated: false,
          error:
            error instanceof ApiError
              ? `${error.message} (HTTP ${error.status})`
              : error instanceof Error
                ? error.message
                : String(error),
          modules: [],
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, authenticated, error, user, install, ssoProviders, gatewayUrl, modules } =
    usePortalData();

  // Only modules the user can actually open participate in navigation. Locked
  // (unlicensed) tiles live on the launcher grid, never in the drawer.
  const openable = useMemo(() => modules.filter((m) => m.state === 'available'), [modules]);

  // Drawer state and breadcrumbs both derive from the URL, so a deep link renders
  // correctly cold and the back button behaves.
  const navLocation = useMemo(
    () => resolveNavigation(location.pathname, openable),
    [location.pathname, openable],
  );

  const breadcrumbs = useMemo(() => buildBreadcrumbs(navLocation), [navLocation]);

  const handleSignOut = useCallback(async () => {
    await api.logout();
    // Back to the root, so the next sign-in starts clean rather than re-entering the
    // screen the previous user was looking at. A demo instance also ends the gateway session
    // (the gateway's /logout), otherwise the gateway would offer the same demo again, signed in.
    window.location.assign(gatewayUrl ? `${gatewayUrl.replace(/\/+$/, '')}/logout` : HOME_PATH);
  }, [gatewayUrl]);

  // The session can end behind a screen that is still showing: signed out in another tab, expired, or
  // a page restored by the Back button from the browser's cache. Leave rather than show stale content.
  useEffect(() => {
    const leave = (): void => {
      if (gatewayUrl) window.location.replace(gatewayUrl);
      else window.location.reload();
    };
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener(SESSION_ENDED_EVENT, leave);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener(SESSION_ENDED_EVENT, leave);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [gatewayUrl]);

  // Demo instances have no login screen of their own: signed-out users go to the gateway.
  useEffect(() => {
    if (!loading && !authenticated && !error && gatewayUrl) window.location.replace(gatewayUrl);
  }, [loading, authenticated, error, gatewayUrl]);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100vh' }} spacing={2}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading platform…
        </Typography>
      </Stack>
    );
  }

  // Signed out: the shell has nothing to show, so the login screen replaces it
  // entirely rather than rendering an empty drawer behind a dialog.
  if (!authenticated && !error) {
    return (
      <LoginPage
        {...(install ? { installName: install.clientName } : {})}
        {...(ssoProviders ? { ssoProviders } : {})}
        // Signing in always lands on the start menu, never back on whatever URL the
        // session expired against. A full navigation rather than a reload, so a deep
        // link a user was refused does not become where they arrive.
        onSignedIn={() => window.location.assign(HOME_PATH)}
      />
    );
  }

  // An administrator-set password is known to someone else, so nothing in the
  // portal is reachable until it is replaced.
  if (user?.mustChangePassword) {
    return (
      <ChangePasswordPage
        displayName={user.displayName}
        onChanged={() => window.location.assign(HOME_PATH)}
        onSignOut={handleSignOut}
      />
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="error">
          <AlertTitle>The portal could not reach the platform API</AlertTitle>
          {error}
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Check that the portal API is running (<code>npm run dev</code> starts both) and that{' '}
            <code>PORTAL_API_PORT</code> matches your <code>.env</code>.
          </Typography>
        </Alert>
      </Box>
    );
  }

  /**
   * The single WORK screen this user can reach, if there is exactly one.
   *
   * Walks the sections the server returned — buckets included — and collects the leaves.
   * Reference screens are excluded: an Application Owner is served the questionnaire plus
   * User Guide and Help & Support, and counting those would mean nobody ever qualifies,
   * which is the bug this replaces. Help is always available; it is not work.
   *
   * Derived from the nav the server actually served, not a hardcoded route, so it cannot
   * drift if the questionnaire ever moves. Anyone with more than one work screen still
   * gets the launcher.
   */
  const onlyScreenPath = (() => {
    const REFERENCE = new Set(['user-guide', 'help-support']);
    const leaves: string[] = [];
    const walk = (sections: readonly { key: string; path: string; sections?: readonly unknown[] }[]) => {
      for (const s of sections) {
        const kids = s.sections as typeof sections | undefined;
        if (kids?.length) walk(kids);
        else if (!REFERENCE.has(s.key)) leaves.push(s.path);
      }
    };
    for (const m of modules) {
      if (m.state !== 'available') continue;
      walk(m.sections as never);
    }
    return leaves.length === 1 ? leaves[0] : null;
  })();

  return (
    <PortalShell
      modules={openable}
      navLocation={navLocation}
      {...(user ? { userId: user.id } : {})}
      brand="Portfolio Platform"
      {...(navLocation.section ? { activeSectionKey: navLocation.section.key } : {})}
      activeTrailKeys={navLocation.trail.map((s) => s.key)}
      breadcrumbs={breadcrumbs}
      onNavigate={(path) => navigate(path)}
      {...(user ? { user: { displayName: user.displayName, email: user.email } } : {})}
      versionLabel={
        install?.platformVersion && install.platformVersion !== '0.0.0'
          ? /^[0-9a-f]{7,40}$/i.test(install.platformVersion)
            ? install.platformVersion.slice(0, 12)
            : install.platformVersion
          : undefined
      }
      onSignOut={handleSignOut}
    >
      <Routes>
        {/*
          An Application Owner has exactly one screen, so the launcher is a menu of one
          they must click through — and the invitation email already tells them they will
          "be automatically directed to the Application Portfolio Q&A screen". This makes
          that true.

          Derived from the nav they are actually served rather than a hardcoded path: the
          server has already filtered their sections down to the single Q&A screen, so
          "the one screen they can see" is the honest definition, and it cannot drift if
          the route ever moves. Anyone with more than one screen still gets the launcher.
        */}
        <Route
          path={HOME_PATH}
          element={
            onlyScreenPath ? (
              <Navigate to={onlyScreenPath} replace />
            ) : (
              <StartMenu
                modules={modules}
                install={install}
                user={user}
                onOpen={(path) => navigate(path)}
              />
            )
          }
        />
        {/* APR resolves its own position from the URL against the catalogue, so these
            differ only in how many segments they match — no route knows a screen.

            Enumerated rather than `/apr/*` because React Router ranks a splat BELOW a
            dynamic segment: `/apr/*` scores 10 and loses to the generic
            `/:moduleId/:sectionKey/:recordId` below at 12, which silently rendered the
            "not built yet" placeholder over Application Master Data. A static first
            segment outranks it at every depth. Deep enough for APR's four nav levels
            plus a record id; a fifth level needs one more line here and nothing else. */}
        <Route
          path="/apr"
          element={
            <AprModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />
          }
        />
        <Route
          path="/apr/:a"
          element={
            <AprModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />
          }
        />
        <Route
          path="/apr/:a/:b"
          element={
            <AprModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />
          }
        />
        <Route
          path="/apr/:a/:b/:c"
          element={
            <AprModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />
          }
        />
        <Route
          path="/apr/:a/:b/:c/:d"
          element={
            <AprModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />
          }
        />
        {/* AI Readiness — enumerated for the same reason as APR above: React Router
            ranks a splat below a dynamic segment, so `/ai_readiness/*` would lose to
            `/:moduleId/:sectionKey` and hand the module to the placeholder. Four levels
            covers Settings → AI Platform Selection → screen, plus a record id. */}
        <Route path="/ai_readiness" element={<AiReadinessModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/ai_readiness/:a" element={<AiReadinessModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/ai_readiness/:a/:b" element={<AiReadinessModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/ai_readiness/:a/:b/:c" element={<AiReadinessModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/portfolio_definition" element={<PortfolioDefinitionModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/portfolio_definition/:a" element={<PortfolioDefinitionModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/portfolio_definition/:a/:b" element={<PortfolioDefinitionModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/portfolio_definition/:a/:b/:c" element={<PortfolioDefinitionModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        {/*
          FOUR AND FIVE LEVELS, because screens open records beneath themselves:
          `opportunities/opportunity-registry/<id>/edit` is four segments, and it had no
          route. React Router fell through to the catch-all and redirected home — which is
          exactly the "Edit reroutes me to the dashboard" the owner reported. Viewing worked
          only because `.../<id>` happens to be three.

          Enumerated by depth rather than a splat for the same reason the other modules are:
          React Router ranks a splat below a dynamic segment, so `/*` would lose to `:a`.
        */}
        <Route path="/portfolio_definition/:a/:b/:c/:d" element={<PortfolioDefinitionModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/portfolio_definition/:a/:b/:c/:d/:e" element={<PortfolioDefinitionModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />

        {/* ERP / Functional Area Consolidation — enumerated by depth for the same reason as
            every other product here: React Router ranks a splat below a dynamic segment, so
            `/erp_consolidation/*` would lose to `/:moduleId/:sectionKey` and hand the module to
            the placeholder. THREE levels covers the module, a screen, and the one record a screen
            opens beneath itself — `initiatives/<id>/setup`, which is four segments. */}
        <Route path="/erp_consolidation" element={<ErpConsolidationModule modules={openable} />} />
        <Route path="/erp_consolidation/:a" element={<ErpConsolidationModule modules={openable} />} />
        <Route path="/erp_consolidation/:a/:b" element={<ErpConsolidationModule modules={openable} />} />
        <Route path="/erp_consolidation/:a/:b/:c" element={<ErpConsolidationModule modules={openable} />} />

        {/* Program Planning & Roadmap — the module, a group and a screen. Enumerated by depth
            for the same reason as the products above. */}
        <Route path="/program_planning" element={<ProgramPlanningModule modules={openable} />} />
        <Route path="/program_planning/:a" element={<ProgramPlanningModule modules={openable} />} />
        <Route path="/program_planning/:a/:b" element={<ProgramPlanningModule modules={openable} />} />

        {/* Four levels: the module, a group, a screen, and Settings' one extra level
            (Settings → Project Execution → Project Visibility). */}
        <Route path="/project_management" element={<ProjectManagementModule modules={openable} />} />
        <Route path="/project_management/:a" element={<ProjectManagementModule modules={openable} />} />
        <Route path="/project_management/:a/:b" element={<ProjectManagementModule modules={openable} />} />
        <Route path="/project_management/:a/:b/:c" element={<ProjectManagementModule modules={openable} />} />
        <Route path="/ai_readiness/:a/:b/:c/:d" element={<AiReadinessModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/ai_readiness/:a/:b/:c/:d/:e" element={<AiReadinessModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        {/* Strategic Portfolio Navigator — enumerated by depth for the same reason as
            APR and AI Readiness: React Router ranks a splat below a dynamic segment, so
            `/spn/*` would lose to `/:moduleId/:sectionKey`. */}
        <Route path="/spn" element={<SpnModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/spn/:a" element={<SpnModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/spn/:a/:b" element={<SpnModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/spn/:a/:b/:c" element={<SpnModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/spn/:a/:b/:c/:d" element={<SpnModule modules={openable} firstName={user?.displayName.split(' ')[0] ?? ''} />} />
        <Route path="/admin" element={<AdminModule modules={openable} />} />
        <Route path="/admin/:sectionKey" element={<AdminModule modules={openable} />} />
        <Route path="/licensing_usage" element={<LicensingModule modules={openable} />} />
        <Route
          path="/licensing_usage/:sectionKey"
          element={<LicensingModule modules={openable} />}
        />
        <Route path="/:moduleId" element={<ModulePlaceholder modules={openable} />} />
        <Route path="/:moduleId/:sectionKey" element={<ModulePlaceholder modules={openable} />} />
        <Route
          path="/:moduleId/:sectionKey/:recordId"
          element={<ModulePlaceholder modules={openable} />}
        />
        <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
      </Routes>
    </PortalShell>
  );
}
